import { LockBreakAnimation } from '../../animations/LockBreakAnimation';
import { app } from '../../app';
import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import type { BlockEntity, LockBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { InvalidEffect } from '../effects/InvalidEffect';
import { CollideHandler, ICollision } from './ICollision';

// fields
//-----------------------------------------------------------------------------
const sounds = ['puzzle-lock-destroy', 'puzzle-lock-poke'];

/*
    lock block collision resolver
*/
export class LockCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: LockBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: LockBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // if basic block collider
    if (
      collider?.type === 'block' &&
      collider.entity.c.block.props.type === 'basic'
    ) {
      // attack lock
      this._attack();
      return true;
    }
    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    if (isOneHitCollider(event.collider?.type)) {
      this._entity.c.blockLock.count = 0;
    }
    this._attack();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack() {
    const component = this._entity.c.blockLock;
    const position = this._entity.c.position.mapPosition;

    // play sound based on component count
    app.sound.play(sounds[component.count], { pan: mapGetPan(position) });

    // if count is 0
    if (component.count <= 0) {
      this.playBreakAnimation(this._entity);
      despawnBlockEntity(this._scene, this._entity);
    } else {
      --component.count;
      this._scene?.events.publish({ id: 'poke' });
    }
  }

  private async playBreakAnimation(entity: BlockEntity) {
    // create  animation
    const animation = new LockBreakAnimation();
    // spawn animation as overlay
    const overlay = spawnOverlayEntity(
      this._scene,
      entity.c.position.mapPosition,
      entity.c.block.props,
      () => animation,
      'foreground',
    );
    // run animation
    await animation.start();
    // despawn entity
    despawnOverlayEntity(this._scene, overlay);
  }
}
